/**
 * Pure helpers for the customer portal (tracker item 45, phase 3): paths,
 * the property filter and its per-device memory, state mapping for API
 * failures, and the small formatters. No React and no API imports (so the
 * HTTP core can use the login path helper without an import cycle).
 */

import {
  PortalInvoiceSummary,
  PortalJob,
  PortalMe,
  PortalProperty,
  PortalRescheduleInput,
  PortalRescheduleRequest,
  PortalServiceRequest,
  PortalServiceRequestInput,
  RescheduleRequestStatus,
  ServiceRequestStatus,
  ServiceRequestTimeOfDay,
} from '../api/types';
import { getErrorCode } from './apiError';
import { formatClock } from './technician';
import axios from 'axios';

/** Where a signed-out customer goes. */
export const PORTAL_LOGIN_PATH = '/portal/login';
export const PORTAL_HOME_PATH = '/portal';

/**
 * Static first segments under /portal. `/portal/:slug` is the tenant landing
 * page (login + register link), so a company slug equal to one of these cannot
 * use the short URL (it still works with /portal/login and the tenant field).
 */
export const PORTAL_RESERVED_SEGMENTS = [
  'login', 'register', 'verify', 'accept-invite', 'forgot-password', 'reset-password',
  'invoices', 'jobs', 'payment-methods', 'statement', 'profile', 'requests', 'request-service', 'messages',
] as const;

export function isPortalPath(pathname: string): boolean {
  return pathname === '/portal' || pathname.startsWith('/portal/');
}

/** The login page to send a signed-out visitor to, by where they were. */
export function loginPathFor(pathname: string): string {
  return isPortalPath(pathname) ? PORTAL_LOGIN_PATH : '/login';
}

/** The company slug in a /portal/:slug... URL, or '' for the reserved pages. */
export function slugFromParam(param: string | undefined): string {
  const value = (param ?? '').trim().toLowerCase();
  if (!value || (PORTAL_RESERVED_SEGMENTS as readonly string[]).includes(value)) return '';
  return /^[a-z0-9][a-z0-9_-]{0,63}$/.test(value) ? value : '';
}

/** The nav of the portal shell. */
export interface PortalNavItem {
  to: string;
  label: string;
  /** Match the exact path only (Home) */
  end?: boolean;
  /** Also available to a login that is not linked to a customer yet (Request service works for a prospect) */
  unlinked?: boolean;
}

export const PORTAL_REQUESTS_PATH = '/portal/requests';
export const PORTAL_REQUEST_SERVICE_PATH = '/portal/request-service';

export const PORTAL_NAV: PortalNavItem[] = [
  { to: '/portal', label: 'Home', end: true, unlinked: true },
  { to: '/portal/invoices', label: 'Invoices' },
  { to: '/portal/jobs', label: 'Jobs' },
  // Estimates to approve or decline (tracker item 39)
  { to: '/portal/estimates', label: 'Estimates' },
  { to: PORTAL_REQUESTS_PATH, label: 'Requests', unlinked: true },
  // Service bundles (tracker item 85): the customer's programs with their timeline and the auto-renew switch
  { to: '/portal/programs', label: 'Programs' },
  // The customer's thread with the office (tracker item 56); the office's notice email links here
  { to: '/portal/messages', label: 'Messages' },
  { to: '/portal/profile', label: 'Profile' },
];

/** Paths an unlinked login may open (everything else goes back to Home): Home and the service request pages. */
export function unlinkedAllowed(pathname: string): boolean {
  const path = pathname.replace(/\/+$/, '') || '/';
  return path === PORTAL_HOME_PATH
    || path === PORTAL_REQUEST_SERVICE_PATH
    || path === PORTAL_REQUESTS_PATH
    || path.startsWith(`${PORTAL_REQUESTS_PATH}/`);
}

// ---------------------------------------------------------------- failures

export type PortalFailureKind = 'rate-limit' | 'disabled' | 'not-linked' | 'session' | 'not-found' | 'generic';

export interface PortalFailure {
  kind: PortalFailureKind;
  message: string;
}

/** How a failed portal call is shown: the kind drives the page (retry, login, "not available"). */
export function describePortalFailure(error: unknown, fallback = 'Something went wrong. Please try again.'): PortalFailure {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    const code = getErrorCode(error);
    const serverMessage = (error.response?.data as { message?: unknown } | undefined)?.message;
    const message = typeof serverMessage === 'string' && serverMessage ? serverMessage : '';
    if (status === 429) {
      return { kind: 'rate-limit', message: 'Too many requests right now. Please wait a few minutes and try again.' };
    }
    if (status === 403 && code === 'PORTAL_DISABLED') {
      return { kind: 'disabled', message: 'The online account is not available right now. Please contact the office.' };
    }
    if (status === 403 && code === 'PORTAL_NOT_LINKED') {
      return { kind: 'not-linked', message: "We're still matching your account to your customer record." };
    }
    if (status === 401) {
      return { kind: 'session', message: 'Your session has ended. Please sign in again.' };
    }
    if (status === 404) {
      return { kind: 'not-found', message: 'We could not find that. It may have been removed.' };
    }
    if (!error.response) {
      return { kind: 'generic', message: 'We could not reach the server. Check your connection and try again.' };
    }
    return { kind: 'generic', message: message || fallback };
  }
  if (error instanceof Error && error.message) return { kind: 'generic', message: error.message };
  return { kind: 'generic', message: fallback };
}

// ---------------------------------------------------------- property filter

export const ALL_PROPERTIES = 'ALL';

const FILTER_KEY_PREFIX = 'crm_portal_property_filter:';

/** The filter is hidden when the customer has a single property (or none). */
export function showPropertyFilter(properties: readonly PortalProperty[]): boolean {
  return properties.length > 1;
}

/** Jobs list only the customer's own sites; invoices also the properties billed to them. */
export function ownedProperties(properties: readonly PortalProperty[]): PortalProperty[] {
  return properties.filter((p) => p.relation === 'OWNED');
}

export function loadPropertyFilter(userId: string): string {
  try {
    return localStorage.getItem(FILTER_KEY_PREFIX + userId) || ALL_PROPERTIES;
  } catch {
    return ALL_PROPERTIES;
  }
}

export function savePropertyFilter(userId: string, value: string): void {
  try {
    if (value === ALL_PROPERTIES) localStorage.removeItem(FILTER_KEY_PREFIX + userId);
    else localStorage.setItem(FILTER_KEY_PREFIX + userId, value);
  } catch {
    /* private mode or blocked storage: the filter just is not remembered */
  }
}

/**
 * The filter to apply: the remembered one if it is still one of the choices,
 * otherwise All. Always All when the filter is not shown.
 */
export function effectivePropertyFilter(stored: string, choices: readonly PortalProperty[]): string {
  if (!showPropertyFilter(choices)) return ALL_PROPERTIES;
  return choices.some((p) => p.id === stored) ? stored : ALL_PROPERTIES;
}

export function propertyIdParam(filter: string): string | undefined {
  return filter === ALL_PROPERTIES ? undefined : filter;
}

// -------------------------------------------------------------- formatting

export function portalAddress(p: { address: string; city: string; state: string; zip: string }): string {
  const region = [p.state, p.zip].filter((v) => v && v.trim()).join(' ');
  return [p.address, p.city, region].filter((v) => v && v.trim()).join(', ');
}

export const PORTAL_JOB_STATUS_LABELS: Record<PortalJob['status'], string> = {
  SCHEDULED: 'Scheduled',
  IN_PROGRESS: 'In progress',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
};

export function portalJobStatusClass(status: PortalJob['status']): string {
  switch (status) {
    case 'COMPLETED': return 'bg-green-100 text-green-800';
    case 'IN_PROGRESS': return 'bg-yellow-100 text-yellow-800';
    case 'CANCELLED': return 'bg-gray-100 text-gray-700';
    default: return 'bg-blue-100 text-blue-800';
  }
}

/** "Tue, Oct 21" for a YYYY-MM-DD date; "Date to be set" when unscheduled. */
export function portalDay(date: string | null | undefined): string {
  if (!date) return 'Date to be set';
  const [y, m, d] = date.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return date;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

/** "Arrive 8:00 AM - 8:30 AM", "Arrive about 8:00 AM", or "Any time that day". */
export function portalWindow(job: Pick<PortalJob, 'scheduledStartTime' | 'arrivalWindowMinutes'>): string {
  if (!job.scheduledStartTime) return 'Any time that day';
  const start = formatClock(job.scheduledStartTime);
  const minutes = job.arrivalWindowMinutes;
  if (!minutes || minutes <= 0) return `Arrive about ${start}`;
  const [h, m] = job.scheduledStartTime.split(':').map(Number);
  const end = h * 60 + m + minutes;
  const endClock = `${String(Math.floor(end / 60) % 24).padStart(2, '0')}:${String(end % 60).padStart(2, '0')}`;
  return `Arrive ${start} - ${formatClock(endClock)}`;
}

export function isOpenJob(job: Pick<PortalJob, 'status'>): boolean {
  return job.status === 'SCHEDULED' || job.status === 'IN_PROGRESS';
}

/** Upcoming visits, soonest first (open jobs on or after `today`, or without a date). */
export function upcomingJobs(jobs: readonly PortalJob[], today: string): PortalJob[] {
  return jobs
    .filter((j) => isOpenJob(j) && (!j.scheduledDate || j.scheduledDate >= today))
    .sort((a, b) => (a.scheduledDate ?? '9999').localeCompare(b.scheduledDate ?? '9999') || (a.scheduledStartTime ?? '').localeCompare(b.scheduledStartTime ?? ''));
}

export function pastJobs(jobs: readonly PortalJob[], today: string): PortalJob[] {
  return jobs
    .filter((j) => !upcomingJobs([j], today).length)
    .sort((a, b) => (b.scheduledDate ?? '').localeCompare(a.scheduledDate ?? ''));
}

export function nextVisit(jobs: readonly PortalJob[], today: string): PortalJob | null {
  return upcomingJobs(jobs, today).find((j) => j.scheduledDate) ?? null;
}

/** Local calendar day as YYYY-MM-DD. */
export function localToday(now: Date = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

export function hasBalance(invoice: Pick<PortalInvoiceSummary, 'balanceDue'>): boolean {
  return Number(invoice.balanceDue) > 0;
}

export function totalBalanceDue(invoices: readonly PortalInvoiceSummary[]): number {
  return Math.round(invoices.reduce((sum, i) => sum + (hasBalance(i) ? Number(i.balanceDue) : 0), 0) * 100) / 100;
}

/** The unpaid invoice that has been waiting longest (earliest due date, then invoice date). */
export function oldestUnpaid(invoices: readonly PortalInvoiceSummary[]): PortalInvoiceSummary | null {
  const unpaid = invoices.filter(hasBalance);
  if (unpaid.length === 0) return null;
  return [...unpaid].sort((a, b) => a.dueDate.localeCompare(b.dueDate) || a.invoiceDate.localeCompare(b.invoiceDate))[0];
}

export function isOverdue(invoice: Pick<PortalInvoiceSummary, 'balanceDue' | 'dueDate'>, today: string): boolean {
  return hasBalance(invoice) && invoice.dueDate.slice(0, 10) < today;
}

export function portalInvoiceStatusLabel(invoice: Pick<PortalInvoiceSummary, 'status' | 'balanceDue' | 'dueDate'>, today: string): string {
  if (Number(invoice.balanceDue) <= 0) return 'Paid';
  if (isOverdue(invoice, today)) return 'Overdue';
  return invoice.status === 'PARTIAL' ? 'Partly paid' : 'Due';
}

export function portalInvoiceStatusClass(label: string): string {
  switch (label) {
    case 'Paid': return 'bg-green-100 text-green-800';
    case 'Overdue': return 'bg-red-100 text-red-800';
    case 'Partly paid': return 'bg-yellow-100 text-yellow-800';
    default: return 'bg-blue-100 text-blue-800';
  }
}

/** Properties shown on the dashboard: one entry per site with the next visit at it. */
export function propertiesWithNextVisit(
  me: Pick<PortalMe, 'properties'>,
  jobs: readonly PortalJob[],
  today: string
): Array<{ property: PortalProperty; next: PortalJob | null }> {
  return me.properties.map((property) => ({
    property,
    next: upcomingJobs(jobs.filter((j) => j.property.id === property.id), today).find((j) => j.scheduledDate) ?? null,
  }));
}

// -------------------------------------------------------- form validation

/** Token from a ?token= query string ('' when absent). */
export function tokenFromSearch(search: string): string {
  return (new URLSearchParams(search).get('token') ?? '').trim();
}

export function passwordsProblem(password: string, confirm: string, strength: (p: string) => string | null): string | null {
  const weak = strength(password);
  if (weak) return weak;
  if (password !== confirm) return 'The two passwords do not match';
  return null;
}

/** Digits only, or '' when it is not 4 digits. */
export function lastFourOf(input: string): string {
  const digits = input.replace(/\D/g, '');
  return digits.length === 4 ? digits : digits.length > 4 ? digits.slice(-4) : '';
}

export interface RegistrationForm {
  tenantSlug: string;
  email: string;
  name: string;
  phone: string;
  invoiceCode: string;
  address: string;
  phoneLast4: string;
  invoiceNumber: string;
  invoiceAmount: string;
}

export const PROOF_FIELD_COUNT_REQUIRED = 2;

/** How many of the three proof facts are filled in (an invoice number OR an amount counts once). */
export function proofFieldCount(form: Pick<RegistrationForm, 'address' | 'phoneLast4' | 'invoiceNumber' | 'invoiceAmount'>): number {
  return (form.address.trim() ? 1 : 0) + (form.phoneLast4.trim() ? 1 : 0) + (form.invoiceNumber.trim() || form.invoiceAmount.trim() ? 1 : 0);
}

/** Field problems of the registration form (empty object = fine). The server answers the same either way. */
export function registrationProblems(form: RegistrationForm, tenantRequired: boolean): Partial<Record<keyof RegistrationForm, string>> {
  const problems: Partial<Record<keyof RegistrationForm, string>> = {};
  if (tenantRequired && !/^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/.test(form.tenantSlug.trim())) problems.tenantSlug = 'Enter your company name as it appears in your invoice link';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) problems.email = 'Enter a valid email address';
  if (!form.name.trim()) problems.name = 'Enter your name';
  const digits = form.phone.replace(/\D/g, '');
  if (!form.phone.trim() || digits.length < 7 || digits.length > 15 || !/^[0-9+().\-\s]+$/.test(form.phone.trim())) problems.phone = 'Enter a phone number';
  if (form.phoneLast4.trim() && form.phoneLast4.replace(/\D/g, '').length < 4) problems.phoneLast4 = 'Enter the last 4 digits of your phone number';
  if (form.invoiceAmount.trim() && !/^\$?\d+(\.\d{1,2})?$/.test(form.invoiceAmount.trim())) problems.invoiceAmount = 'Enter an amount like 125.50';
  return problems;
}

/** The request body: blank optional fields are left out. */
export function registrationRequest(form: RegistrationForm, tenantSlug: string) {
  const optional = (value: string) => value.trim() || undefined;
  return {
    tenantSlug: tenantSlug.trim().toLowerCase(),
    email: form.email.trim(),
    name: form.name.trim(),
    phone: form.phone.trim(),
    invoiceCode: optional(form.invoiceCode),
    address: optional(form.address),
    phoneLast4: form.phoneLast4.trim() ? lastFourOf(form.phoneLast4) || optional(form.phoneLast4) : undefined,
    invoiceNumber: optional(form.invoiceNumber),
    invoiceAmount: form.invoiceAmount.trim() ? form.invoiceAmount.trim().replace(/^\$/, '') : undefined,
  };
}

/**
 * Whether a failed pay call may be repeated with the SAME card token. The
 * backend's idempotency key follows the token, so resending it is safe when we
 * never learned the outcome (no answer, a server error) or the call was
 * refused before anything ran (429). Other failures (validation, not found,
 * conflict) used the token up or never will work: take a new card entry.
 */
export function canRetryWithSameToken(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  const status = error.response?.status;
  return !error.response || status === 429 || (status !== undefined && status >= 500);
}

// ------------------------------------------------- requests (phase 4)

export const SERVICE_REQUEST_STATUS_LABELS: Record<ServiceRequestStatus, string> = {
  NEW: 'Received',
  CONVERTED: 'Accepted',
  DECLINED: 'Declined',
};

export function serviceRequestStatusClass(status: ServiceRequestStatus): string {
  switch (status) {
    case 'CONVERTED': return 'bg-green-100 text-green-800';
    case 'DECLINED': return 'bg-red-100 text-red-800';
    default: return 'bg-blue-100 text-blue-800';
  }
}

/** What the customer is told about each status. */
export function serviceRequestStatusNote(request: Pick<PortalServiceRequest, 'status' | 'declineReason'>, companyName: string): string {
  switch (request.status) {
    case 'CONVERTED': return `${companyName} accepted your request and will schedule it. You will see the visit under Jobs once it is booked.`;
    case 'DECLINED': return request.declineReason ? `${companyName} could not take this request: ${request.declineReason}` : `${companyName} could not take this request.`;
    default: return `${companyName} has your request and will reply soon.`;
  }
}

export const TIME_OF_DAY_LABELS: Record<ServiceRequestTimeOfDay, string> = {
  ANY: 'Any time',
  MORNING: 'Morning',
  AFTERNOON: 'Afternoon',
  EVENING: 'Evening',
};

export const RESCHEDULE_STATUS_LABELS: Record<RescheduleRequestStatus, string> = {
  OPEN: 'Waiting for the office',
  AUTO_APPROVED: 'Approved',
  APPROVED: 'Approved',
  DECLINED: 'Declined',
  CANCELLED: 'Cancelled',
};

export function rescheduleStatusClass(status: RescheduleRequestStatus): string {
  switch (status) {
    case 'AUTO_APPROVED':
    case 'APPROVED': return 'bg-green-100 text-green-800';
    case 'DECLINED': return 'bg-red-100 text-red-800';
    case 'CANCELLED': return 'bg-gray-100 text-gray-700';
    default: return 'bg-yellow-100 text-yellow-800';
  }
}

/** The line a customer reads for a reschedule request: what was asked, and how it ended. */
export function rescheduleOutcome(request: PortalRescheduleRequest, companyName: string): string {
  const asked = `${portalDay(request.requestedDate)}${request.requestedStartTime ? ` at ${formatClock(request.requestedStartTime)}` : ''}`;
  switch (request.status) {
    case 'AUTO_APPROVED':
    case 'APPROVED': {
      const date = request.approvedDate ?? request.requestedDate;
      const time = request.approvedStartTime ?? request.requestedStartTime;
      return `Approved: your visit is now ${portalDay(date)}${time ? ` at ${formatClock(time)}` : ''}.`;
    }
    case 'DECLINED':
      return request.declineReason ? `${companyName} could not move the visit: ${request.declineReason}` : `${companyName} could not move the visit.`;
    case 'CANCELLED': return `You cancelled the request for ${asked}.`;
    default: return `You asked for ${asked}. ${companyName} will confirm it.`;
  }
}

export interface RequestFailure {
  /** The page-level state (rate limit, expired session, ...) or a form problem */
  kind: PortalFailureKind | 'limit' | 'conflict' | 'validation';
  message: string;
  /** The server answered that the thing changed under us: reload what is shown */
  reload?: boolean;
}

/**
 * How a failed request call (service request or reschedule) is told to the customer:
 * the 409 codes of the reschedule rules in plain words, the 20-open-requests limit, 429, and
 * the server's own message for a rejected form. `leadHours` and `company` fill the rule text.
 */
export function describeRequestFailure(error: unknown, context: { leadHours?: number; company?: string } = {}): RequestFailure {
  const base = describePortalFailure(error, 'We could not send your request. Please try again.');
  if (!axios.isAxiosError(error)) return base;
  const status = error.response?.status;
  const code = getErrorCode(error);
  const company = context.company || 'the office';
  if (status === 429) return { kind: 'rate-limit', message: 'You have sent a lot of requests in a short time. Please wait a while and try again, or call the office.' };
  if (status === 409) {
    switch (code) {
      case 'TOO_MANY_OPEN_REQUESTS':
        return { kind: 'limit', message: 'You already have 20 open requests. Please wait for a reply to some of them before sending another, or call the office.' };
      case 'JOB_NOT_RESCHEDULABLE':
        return { kind: 'conflict', message: 'This visit can no longer be rescheduled online.', reload: true };
      case 'JOB_NOT_SCHEDULED':
        return { kind: 'conflict', message: `This visit does not have a date yet, so there is nothing to reschedule. Please call ${company} if you need to change it.`, reload: true };
      case 'RESCHEDULE_TOO_LATE':
        return {
          kind: 'conflict',
          message: `Visits can only be rescheduled online at least ${context.leadHours ?? 48} hours ahead, and this one is too close. Please call ${company}.`,
        };
      case 'RESCHEDULE_DATE_NOT_FUTURE':
        return { kind: 'validation', message: 'Please choose a date after today.' };
      case 'RESCHEDULE_NO_CHANGE':
        return { kind: 'validation', message: 'The visit is already booked for that date and time. Choose a different date or time.' };
      case 'RESCHEDULE_REQUEST_OPEN':
        return { kind: 'conflict', message: 'You already have a reschedule request waiting for this visit. Cancel it first if you want to send a different one.', reload: true };
      case 'RESCHEDULE_REQUEST_NOT_OPEN':
        return { kind: 'conflict', message: 'That request has already been answered or cancelled.', reload: true };
      default:
        return { ...base, kind: 'conflict' };
    }
  }
  if (status === 400) return { kind: 'validation', message: base.message };
  if (status === 404) return { kind: 'not-found', message: 'We could not find that. It may have been changed; please reload the page and try again.', reload: true };
  return base;
}

/** The next calendar day after a YYYY-MM-DD date. */
export function dayAfter(date: string): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

/** A real YYYY-MM-DD calendar date. */
export function isCalendarDate(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) && new Date(`${value}T00:00:00Z`).toISOString().slice(0, 10) === value;
}

/**
 * Whether the visit looks closer than the lead time (a hint only: the server decides, in the
 * company timezone). The date and time are read in the browser's timezone.
 */
export function looksTooLateToReschedule(job: Pick<PortalJob, 'scheduledDate' | 'scheduledStartTime'>, leadHours: number, now: Date = new Date()): boolean {
  if (!job.scheduledDate) return false;
  const start = new Date(`${job.scheduledDate}T${job.scheduledStartTime ?? '00:00'}:00`).getTime();
  return (start - now.getTime()) / 3_600_000 < leadHours;
}

/** The reschedule button: a SCHEDULED visit (the portal shows work-order-generated ones as scheduled) with a date. */
export function canAskToReschedule(job: Pick<PortalJob, 'status' | 'scheduledDate'>): boolean {
  return job.status === 'SCHEDULED' && !!job.scheduledDate;
}

export interface RescheduleForm {
  date: string;
  startTime: string;
  window: string;
  reason: string;
}

export const EMPTY_RESCHEDULE_FORM: RescheduleForm = { date: '', startTime: '', window: '', reason: '' };
export const RESCHEDULE_WINDOWS = ['', 'Morning', 'Afternoon', 'Evening'] as const;

export function rescheduleProblems(form: RescheduleForm, today: string): Partial<Record<keyof RescheduleForm, string>> {
  const problems: Partial<Record<keyof RescheduleForm, string>> = {};
  if (!form.date) problems.date = 'Choose the date you would like';
  else if (!isCalendarDate(form.date)) problems.date = 'Enter a valid date';
  else if (form.date <= today) problems.date = 'Choose a date after today';
  if (form.startTime && !/^([01]\d|2[0-3]):[0-5]\d$/.test(form.startTime)) problems.startTime = 'Enter a time like 09:30';
  if (form.reason.length > 500) problems.reason = 'Please keep it under 500 characters';
  return problems;
}

export function rescheduleRequestBody(form: RescheduleForm): PortalRescheduleInput {
  return {
    requestedDate: form.date,
    ...(form.startTime ? { requestedStartTime: form.startTime } : {}),
    ...(form.window ? { requestedWindow: form.window } : {}),
    ...(form.reason.trim() ? { reason: form.reason.trim() } : {}),
  };
}

export const NEW_ADDRESS = 'NEW';
export const MAX_PREFERRED_DATES = 5;

export interface ServiceRequestForm {
  /** An OWNED property id, or NEW_ADDRESS for a typed address */
  place: string;
  address: string;
  itemId: string;
  dates: string[];
  timeOfDay: ServiceRequestTimeOfDay | '';
  notes: string;
  contactName: string;
  contactPhone: string;
}

export function emptyServiceRequestForm(defaults: { place?: string; contactName?: string; contactPhone?: string } = {}): ServiceRequestForm {
  return {
    place: defaults.place ?? '',
    address: '',
    itemId: '',
    dates: [''],
    timeOfDay: '',
    notes: '',
    contactName: defaults.contactName ?? '',
    contactPhone: defaults.contactPhone ?? '',
  };
}

/** Whether the form asks for a typed address: an unlinked login, a customer with no own property, or "a different address". */
export function usesTypedAddress(form: Pick<ServiceRequestForm, 'place'>, linked: boolean, hasOwnedProperties: boolean): boolean {
  return !linked || !hasOwnedProperties || form.place === NEW_ADDRESS;
}

/** Field problems of the request form (empty = fine). `linked`: the login has a customer and may pick a property. */
export function serviceRequestProblems(form: ServiceRequestForm, linked: boolean, today: string, hasOwnedProperties: boolean): Partial<Record<keyof ServiceRequestForm, string>> {
  const problems: Partial<Record<keyof ServiceRequestForm, string>> = {};
  if (usesTypedAddress(form, linked, hasOwnedProperties)) {
    if (!form.address.trim()) problems.address = 'Enter the address where you need the service';
    else if (form.address.trim().length > 300) problems.address = 'Please keep the address under 300 characters';
  } else if (!form.place) {
    problems.place = 'Choose the property';
  }
  if (!form.itemId && !form.notes.trim()) problems.notes = 'Choose a service or tell us what you need';
  if (form.notes.length > 2000) problems.notes = 'Please keep the notes under 2000 characters';
  const dates = form.dates.filter((d) => d);
  if (dates.some((d) => !isCalendarDate(d))) problems.dates = 'Enter valid dates';
  else if (dates.some((d) => d < today)) problems.dates = 'Dates cannot be in the past';
  if (!linked) {
    if (!form.contactName.trim()) problems.contactName = 'Enter your name';
    if (form.contactPhone.trim() && form.contactPhone.replace(/\D/g, '').length < 7) problems.contactPhone = 'Enter a phone number we can call';
  }
  return problems;
}

/** The request body: blank optional fields are left out; contact details only for an unlinked login. */
export function serviceRequestBody(form: ServiceRequestForm, linked: boolean, hasOwnedProperties: boolean): PortalServiceRequestInput {
  const dates = [...new Set(form.dates.filter((d) => d))].sort();
  return {
    ...(usesTypedAddress(form, linked, hasOwnedProperties) ? { serviceAddress: form.address.trim() } : { propertyId: form.place }),
    ...(form.itemId ? { requestedItemId: form.itemId } : {}),
    ...(dates.length ? { preferredDates: dates } : {}),
    ...(form.timeOfDay ? { preferredTimeOfDay: form.timeOfDay } : {}),
    ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
    ...(!linked && form.contactName.trim() ? { contactName: form.contactName.trim() } : {}),
    ...(!linked && form.contactPhone.trim() ? { contactPhone: form.contactPhone.trim() } : {}),
  };
}

/** "Mon, Oct 27 · Morning" style line for the dates a customer would like. */
export function preferredTimesLabel(request: Pick<PortalServiceRequest, 'preferredDates' | 'preferredTimeOfDay'>): string {
  const dates = request.preferredDates.map((d) => portalDay(d));
  const time = request.preferredTimeOfDay && request.preferredTimeOfDay !== 'ANY' ? TIME_OF_DAY_LABELS[request.preferredTimeOfDay] : '';
  return [dates.join(', '), time].filter(Boolean).join(' · ');
}
